/**
 * Helpers for the structured verification workflow's draft/resume feature.
 * Pure functions so they can be unit-tested without rendering the component.
 */

import { isVideoUrl } from './fileValidation';

function safeParseArray(value) {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Turn a loaded verification's stored photos (array or JSON string of photo
 * objects) into the shape AIPhotoAnalyzer / the workflow hold in state.
 * Photos without a usable URL are dropped.
 */
export function parseVerificationPhotos(vData) {
  if (!vData) return [];
  const raw = Array.isArray(vData.photos)
    ? vData.photos
    : (typeof vData.photos === 'string' ? safeParseArray(vData.photos) : []);

  return raw
    .map((p, idx) => {
      const photoUrl = p.photoUrl || p.url || null;
      return {
        id: p.id || p.savedId || `saved_${idx}`,
        file: null,
        name: p.name || 'Photo',
        previewUrl: p.thumbnailUrl || photoUrl,
        photoUrl,
        thumbnailUrl: p.thumbnailUrl || null,
        status: 'saved',
        analysis: p.aiAnalysis || p.analysis || null,
        savedId: p.savedId || (typeof p.id === 'number' ? p.id : null),
        isVideo: p.isVideo || isVideoUrl(photoUrl),
      };
    })
    .filter((p) => !!p.photoUrl);
}

/**
 * Build the photos payload for draft save / final submit, keeping only photos
 * backed by a durable URL (never blob:/data:, which die on reload).
 */
export function sanitizePhotosForSubmit(photos) {
  return (photos || [])
    .map((p) => {
      const url = p.photoUrl || p.url || null;
      return {
        id: p.id,
        name: p.name,
        url,
        photoUrl: url,
        thumbnailUrl: p.thumbnailUrl || null,
        aiAnalysis: p.analysis || p.aiAnalysis || null,
        analysis: p.analysis || p.aiAnalysis || null,
        isVideo: p.isVideo || isVideoUrl(url),
      };
    })
    .filter((p) => p.url && !String(p.url).startsWith('blob:') && !String(p.url).startsWith('data:'));
}
